import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { CalendarDays } from 'lucide-react';
import { countWorkingDays } from '../../utils/vacationDays';

export interface NewVacationRequestValues {
  startDate: string;
  endDate: string;
  notes: string;
}

interface NewVacationRequestFormProps {
  isSubmitting?: boolean;
  serverError?: string | null;
  onSubmit: (values: NewVacationRequestValues) => void;
  onCancel: () => void;
}

export const NewVacationRequestForm: React.FC<NewVacationRequestFormProps> = ({
  isSubmitting,
  serverError,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation('vacations');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const workingDaysPreview = useMemo(
    () => countWorkingDays(startDate, endDate),
    [startDate, endDate],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      setError(t('form.error.datesRequired'));
      return;
    }
    if (endDate < startDate) {
      setError(t('form.error.endBeforeStart'));
      return;
    }
    setError('');
    onSubmit({ startDate, endDate, notes: notes.trim() });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="new-vacation-form">
      {(error || serverError) && (
        <Alert variant="destructive" data-testid="new-vacation-error">
          <AlertDescription>{error || serverError}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="vacation-start-date">{t('form.startDate')}</Label>
          <Input
            id="vacation-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="new-vacation-start-date"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vacation-end-date">{t('form.endDate')}</Label>
          <Input
            id="vacation-end-date"
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            data-testid="new-vacation-end-date"
          />
        </div>
      </div>

      <div
        className="flex items-center gap-2 rounded-md bg-muted p-3 text-sm"
        data-testid="new-vacation-working-days"
      >
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        <span className="text-muted-foreground">{t('form.workingDaysPreview')}:</span>
        <span className="font-semibold">{workingDaysPreview}</span>
        <span className="text-xs text-muted-foreground">({t('form.workingDaysHint')})</span>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="vacation-notes">{t('form.notes')}</Label>
        <Textarea
          id="vacation-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('form.notesPlaceholder')}
          maxLength={2000}
          data-testid="new-vacation-notes"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t('form.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting} data-testid="new-vacation-submit">
          {isSubmitting ? t('form.submitting') : t('form.submit')}
        </Button>
      </div>
    </form>
  );
};
